import { IAddressOption } from "@faire/retailer-visitor-shared/components/GoogleMapsComponent/CustomAddressDropdown";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import findResidentialAddress from "@faire/web-api--source/endpoints/www/api/retailer/find-address/get";
import findBusinesses, {
  QueryParameters,
} from "@faire/web-api--source/endpoints/www/api/retailer/v2/find-businesses/get";
import { IMatchedAddress } from "@faire/web-api--source/indigofair/data/IMatchedAddress";
import { IMatchedBusiness } from "@faire/web-api--source/indigofair/data/IMatchedBusiness";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import { useCallback, useMemo, useState } from "react";

export const useAutocompleteAddressInput = (
  type: AddressType,
  countryRestriction?: keyof typeof ISO3166Alpha3,
  onChange?: (value: string) => void,
  onCannotFindClick?: () => void,
  onSelect?: (placeId: string) => void,
) => {
  const [autocompleteResults, setAutocompleteResults] = useState<IResult[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { retailerCountry } = useNationality();

  const autocompleteOptions = useMemo(() => {
    if (type === AddressType.RESIDENTIAL) {
      const results = autocompleteResults as IMatchedAddress[];
      return results.map((result: IMatchedAddress) => ({
        id: result.place_id ?? "",
        name: result.display_text,
        location: result.location,
      }));
    }

    const results = autocompleteResults as IMatchedBusiness[];
    return results.map((result: IMatchedBusiness) => ({
      ...result,
      id: result.id ?? "",
    }));
  }, [autocompleteResults, type]);

  const fetchAutocompleteResults = useCallback(
    async (query?: string) => {
      setIsLoading(true);
      if (type === AddressType.BUSINESS) {
        const results = await findBusinesses(
          QueryParameters.build({
            query,
            country: countryRestriction ?? retailerCountry,
          })
        );
        setAutocompleteResults(results.businesses);
      } else {
        const results = await findResidentialAddress(
          QueryParameters.build({
            query,
            country: countryRestriction ?? retailerCountry,
          })
        );
        setAutocompleteResults(results);
      }
      setIsLoading(false);
    },
    [type, retailerCountry, countryRestriction]
  );

  const showDropdown = useCallback(() => setIsDropdownVisible(true), []);
  const hideDropdown = useCallback(() => setIsDropdownVisible(false), []);

  const handleOptionSelect = useCallback(
    (option: IAddressOption) => {
      hideDropdown();
      onChange?.(`${option.location}`);
      if (option.id) {
        onSelect?.(option.id);
      }
    },
    [hideDropdown, onChange, onSelect]
  );

  const handleCannotFindClick = useCallback(() => {
    hideDropdown();
    onCannotFindClick?.();
  }, [hideDropdown, onCannotFindClick]);

  return {
    autocompleteOptions,
    isDropdownVisible,
    isLoading,
    fetchAutocompleteResults,
    showDropdown,
    hideDropdown,
    handleOptionSelect,
    handleCannotFindClick,
  };
};

type IResult = IMatchedAddress | IMatchedBusiness;

export enum AddressType {
  BUSINESS = "BUSINESS",
  RESIDENTIAL = "RESIDENTIAL",
}
